import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { 
  User, 
  AuthResponse, 
  LoginDto, 
  RegisterDto, 
  ResetPasswordRequestDto, 
  ResetPasswordDto, 
  ChangePasswordDto 
} from '../types';
import { authService } from '../services/authService';
import toast from 'react-hot-toast';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (credentials: LoginDto) => Promise<void>;
  register: (userData: RegisterDto) => Promise<void>;
  logout: () => Promise<void>;
  updateUser: (user: User) => void;
  verifyEmail: (token: string) => Promise<void>;
  resendVerificationEmail: (email: string) => Promise<void>;
  requestPasswordReset: (email: string) => Promise<void>;
  resetPassword: (resetData: ResetPasswordDto) => Promise<void>;
  changePassword: (changeData: ChangePasswordDto) => Promise<void>;
  refreshAuth: () => Promise<void>;
  isEmailVerified: boolean;
  hasRole: (role: string) => boolean;
  isAdmin: boolean;
  isModerator: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initAuth = async () => {
      try {
        const token = authService.getToken();
        if (token) {
          // First, load user from localStorage immediately for faster UI update
          const cachedUser = authService.getCurrentUser();
          if (cachedUser) {
            setUser(cachedUser);
            setLoading(false); // Set loading to false immediately with cached user
          }
          
          // Then validate token with API in the background
          try {
            const currentUser = await authService.getProfile();
            setUser(currentUser);
          } catch (apiError) {
            console.error('Failed to validate token with API:', apiError);
            // If API fails but we have cached user, keep using cached user
            // Only clear tokens if we don't have cached user or if it's a 401/403 error
            if (!cachedUser || (apiError as any)?.response?.status === 401 || (apiError as any)?.response?.status === 403) {
              authService.clearTokens();
              setUser(null);
            }
          }
        }
      } catch (error) {
        console.error('Failed to initialize auth:', error);
        authService.clearTokens();
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    initAuth();
  }, []);

  const login = useCallback(async (credentials: LoginDto) => {
    try {
      setLoading(true);
      const response: AuthResponse = await authService.login(credentials);
      setUser(response.user);
      
      if (!response.user.emailVerified) {
        toast.success('Login successful! Please verify your email to access all features.');
      } else {
        toast.success('Welcome back!');
      }
    } catch (error: any) {
      const message = error.response?.data?.message || 'Falha no login';
      toast.error(message);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  const register = useCallback(async (userData: RegisterDto) => {
    try {
      setLoading(true);
      const response: AuthResponse = await authService.register(userData);
      setUser(response.user);
      toast.success(response.message || 'Account created successfully! Please check your email to verify your account.');
    } catch (error: any) {
      const message = error.response?.data?.message || 'Falha no cadastro';
      toast.error(message);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  const verifyEmail = useCallback(async (token: string) => {
    try {
      setLoading(true);
      const response = await authService.verifyEmail(token);
      toast.success(response.message);
      
      // Update user's emailVerified status
      if (user) {
        const updatedUser = { ...user, emailVerified: true };
        setUser(updatedUser);
        authService.setUser(updatedUser);
      }
    } catch (error: any) {
      const message = error.response?.data?.message || 'Falha na verificação de email';
      toast.error(message);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [user]);

  const resendVerificationEmail = useCallback(async (email: string) => {
    try {
      setLoading(true);
      const response = await authService.resendVerificationEmail(email);
      toast.success(response.message);
    } catch (error: any) {
      const message = error.response?.data?.message || 'Falha ao reenviar email de verificação';
      toast.error(message);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  const requestPasswordReset = useCallback(async (email: string) => {
    try {
      setLoading(true);
      const response = await authService.requestPasswordReset(email);
      toast.success(response.message);
    } catch (error: any) {
      const message = error.response?.data?.message || 'Falha ao enviar email de redefinição';
      toast.error(message);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  const resetPassword = useCallback(async (resetData: ResetPasswordDto) => {
    try {
      setLoading(true);
      const response = await authService.resetPassword(resetData);
      toast.success(response.message);
    } catch (error: any) {
      const message = error.response?.data?.message || 'Falha na redefinição de senha';
      toast.error(message);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  const changePassword = useCallback(async (changeData: ChangePasswordDto) => {
    try {
      setLoading(true);
      const response = await authService.changePassword(changeData);
      toast.success(response.message);
    } catch (error: any) {
      const message = error.response?.data?.message || 'Falha na alteração de senha';
      toast.error(message);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  const refreshAuth = useCallback(async () => {
    try {
      const currentUser = await authService.getProfile();
      setUser(currentUser);
    } catch (error) {
      console.error('Failed to refresh auth:', error);
      authService.clearTokens();
      setUser(null);
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await authService.logout();
      setUser(null);
      toast.success('Logged out successfully');
    } catch (error) {
      console.error('Logout error:', error);
      // Clear local state even if API call fails
      authService.clearTokens();
      setUser(null);
      toast.success('Logged out successfully');
    }
  }, []);

  const updateUser = useCallback((updatedUser: User) => {
    setUser(updatedUser);
    authService.setUser(updatedUser);
  }, []);

  // Computed properties
  const isEmailVerified = user?.emailVerified || false;
  const hasRole = useCallback((role: string) => authService.hasRole(role), []);
  const isAdmin = authService.isAdmin();
  const isModerator = authService.isModerator();

  const value = {
    user,
    loading,
    login,
    register,
    logout,
    updateUser,
    verifyEmail,
    resendVerificationEmail,
    requestPasswordReset,
    resetPassword,
    changePassword,
    refreshAuth,
    isEmailVerified,
    hasRole,
    isAdmin,
    isModerator,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
